import {html, render as renderHTML} from 'lit-html'
import {install, uninstall} from '@github-ui/hotkey'
import type {CheckStepElement} from './check-step'
import {LogParser, setIncrementalParsingEnabled} from './log-parser'
import type {LogLike} from './log-parser'
import {NodeType} from './parser-types'
import {setCompositeActionsEnabled} from './parser'
import {areCharacterKeyShortcutsEnabled} from '@github-ui/hotkey/keyboard-shortcuts-helper'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {observe} from '@github-ui/selector-observer'
import {on} from 'delegated-events'
import {repeat} from 'lit-html/directives/repeat'
import {sendCustomMetric, sendStats} from '@github-ui/stats'
import {
  updateAutoScrollState,
  isAutoScrollEnabled,
  determineScrollDirection,
  getElementToScroll,
  setElementToScroll,
  resetScrollState,
} from './auto-scroll'

export {disableAutoScroll} from './auto-scroll'

const MAX_LOG_SIZE_IN_BYTES = 1024 * 1024 * 10 // 10Mb
// While streaming a completed log, flush to the DOM once this many decoded
// characters have accumulated since the last flush. Bounds the work done before
// the first paint so a large log renders its first screenful quickly instead of
// blocking on a single whole-log parse + cluster build. Only used when both the
// incremental-parsing and progressive-ingestion flags are on (see streamCompletedLog).
const PROGRESSIVE_FLUSH_THRESHOLD = 256 * 1024 // ~256Kb
const LINES_PER_CLUSTER = 50
const ESTIMATED_LINE_LENGTH = 20
// Set when we programmatically adjust the scroll position to keep the viewport
// anchored while virtualizing clusters, so the scroll listener doesn't mistake
// the adjustment for a user-initiated scroll (which would corrupt the
// auto-scroll / scroll-direction heuristics).
let suppressScrollDirection = false

export const stepsMap: {[externalId: string]: Step} = {}

export function getUnorderedSteps(): Step[] {
  return Object.keys(stepsMap).map(x => stepsMap[x]!)
}

const embeddingContainer = () => document.querySelector('.content-preview-content') as HTMLElement

const scrollableContainer = () =>
  embeddingContainer() || (document.querySelector('.js-full-logs-container') as HTMLElement)
observe('.js-full-logs-container', {
  constructor: HTMLElement,
  add() {
    calculateContainerHeights()
    addScrollListener()
  },
})

const uxRefresh = () => {
  const logsContainer = scrollableContainer()
  const logsContainerHasUxRefreshAttribute = logsContainer?.hasAttribute('data-ux-refresh')
  return logsContainer && logsContainerHasUxRefreshAttribute
}

function scrollListener() {
  if (suppressScrollDirection) {
    // This scroll event was triggered by our own anchor compensation, not the
    // user. Don't update the direction heuristic, just keep the state in sync.
    suppressScrollDirection = false
    resetScrollState()
    determineScrollDirection(calculateScrollTop())
  } else {
    determineScrollDirection(calculateScrollTop())
  }
  renderSteps()
}

function addScrollListener() {
  const container = uxRefresh() ? document : scrollableContainer() || document
  /* eslint-disable-next-line github/prefer-observers */
  container.addEventListener('scroll', scrollListener)
}

function calculateScrollTop(): number {
  // Read from the same active scroll source that renderCluster/adjustScrollBy use:
  // the window only in UX-refresh inline mode, otherwise the log container
  // (fullscreen and the legacy inline viewer both scroll the container). Using
  // window.scrollY for the legacy inline viewer would always read 0 and break the
  // scroll-direction heuristic after anchor compensation.
  return uxRefresh() && !isFullscreen() ? window.scrollY : fullLogsContainer().scrollTop
}

function calculateContainerHeights() {
  if (uxRefresh() && !isFullscreen()) {
    scrollableContainer()?.style.removeProperty('height')
  } else {
    makeScrollable(scrollableContainer())
  }
}

function makeScrollable(element: HTMLElement | null) {
  if (!element) return
  element.style.height = `${window.innerHeight - element.getBoundingClientRect().top}px`
}

calculateContainerHeights()
addScrollListener()

/* eslint-disable-next-line github/prefer-observers */
window.addEventListener('resize', calculateContainerHeights)

export const StepState = {
  INITIAL: 0,
  COMPLETED_LOGS_LOADING: 1,
  COMPLETED_LOGS_LOADED: 2,
  STREAMING: 3,
  ERROR: 4,
} as const

export type StepState = (typeof StepState)[keyof typeof StepState]

type Cluster = {
  startIndex: number
  endIndex: number
  wrapper: HTMLElement
  visible: boolean
  height?: number
  // Number of lines in this cluster that are currently renderable (not hidden by
  // a collapsed group/action). Kept in sync by estimateClusterHeight. A value of
  // 0 means the cluster has nothing to show and must never be materialized.
  visibleLineCount?: number
}

type Step = {
  externalId: string
  number: number
  parser: LogParser
  container: HTMLElement
  searchLineIndexes: number[]
  clusters: Cluster[]
  state: StepState
  permalinkLine?: number
  expandedGroups: Set<number>
  /** Tracks which composite action bars are expanded, by their start line index */
  expandedActions: Set<number>
  /** Tracks how many completed action indices were already re-rendered */
  lastCompletedActionCount: number
}

const {href, hash} = window.location
const baseURL = href.substring(0, href.length - hash.length)

const buffer: Record<string, string[]> = {}

export function flushLogs(step: Step) {
  const newLines = buffer[step.externalId]
  if (!newLines) return
  for (const stp of Object.values(stepsMap)) {
    if (step !== stp && !stp.container.hasAttribute('manually-opened')) closeStep(stp)
  }
  updateStreamingLogs(step, newLines, null)
  delete buffer[step.externalId]
}

export function bufferLogs(externalId: string, lines: string[]) {
  buffer[externalId] = (buffer[externalId] || []).concat(lines)
}

export function bufferedStepIds() {
  return Object.keys(buffer)
}

let compositeActionsInitialized = false

export function configureStep(container: HTMLElement, permalinkLine?: number): Step {
  if (!compositeActionsInitialized) {
    compositeActionsInitialized = true
    // eslint-disable-next-line github/get-attribute -- attribute name matches the feature flag name for searchability
    setCompositeActionsEnabled(!!scrollableContainer()?.hasAttribute('data-actions_runner_emit_composite_markers'))
    setIncrementalParsingEnabled(isFeatureEnabled('actions_incremental_log_parsing'))
  }
  const number = getStepNumber(container)
  const externalId = container.getAttribute('data-external-id') || ''
  const parser = new LogParser('', `${baseURL}#step:${number}`)

  const step: Step = {
    externalId,
    number,
    parser,
    container,
    searchLineIndexes: [],
    state: StepState.INITIAL,
    clusters: [],
    permalinkLine,
    expandedGroups: new Set<number>(),
    expandedActions: new Set<number>(),
    lastCompletedActionCount: 0,
  }

  moveStep(container)
  stepsMap[externalId] = step
  container.removeEventListener('toggle', handleToggle)
  container.addEventListener('toggle', handleToggle)
  container.removeEventListener('click', handleClick)
  container.addEventListener('click', handleClick)

  flushLogs(step)
  return step
}

function stepFromContainer(container: HTMLElement): Step | null {
  const externalId = container.getAttribute('data-external-id')
  if (!externalId) {
    return null
  }

  return stepsMap[externalId] || null
}

async function handleToggle(event: Event) {
  const container = event.currentTarget as HTMLElement
  const step = stepFromContainer(container)
  if (!step) {
    return
  }

  // This shouldn't be necessary, but we are seeing that heights sometimes are not calculated correctly when the page loads
  calculateContainerHeights()

  if (isOpen(step) && hasInlineDetails(step)) {
    step.state = StepState.COMPLETED_LOGS_LOADED
  } else if (isOpen(step) && step.state !== StepState.COMPLETED_LOGS_LOADED) {
    await loadCompletedLogs(step)
  }
  renderSteps()
}

function handleClick(event: Event) {
  const container = event.currentTarget as HTMLElement
  const step = stepFromContainer(container)
  if (!step) {
    return
  }

  if (!hasInlineDetails(step) && step.state !== StepState.STREAMING && !getCompletedLogURL(step)) {
    event.preventDefault()
    return
  }

  if (!step.container.hasAttribute('open')) {
    step.container.setAttribute('manually-opened', '')
  }
}

function isOpen(step: Step) {
  return step.container.hasAttribute('open')
}

function openStep(step: Step) {
  resetScrollToLogic()
  step.container.setAttribute('open', 'open')
}

export function expandStep(step: Step) {
  openStep(step)
}

function closeStep(step: Step) {
  step.container.removeAttribute('open')
  const element = step.container.closest<CheckStepElement>('check-step')
  if (element) {
    element.removeAttribute('data-in-progress')
    element.removeAttribute('manually-opened')
    element.update()
  }
}

function getCompletedLogURL(step: Step) {
  return step.container.getAttribute('data-log-url')
}

function stepAttribute(step: Step, name: string) {
  return (
    step.container.getAttribute(name) ||
    step.container.closest<CheckStepElement>('check-step')?.getAttribute(name) ||
    ''
  )
}

function hasInlineDetails(step: Step) {
  const stepType = stepAttribute(step, 'data-step-type')
  return stepType === 'wait' || stepType === 'wait-all' || stepType === 'cancel'
}

function isBackgroundStep(step: Step) {
  return step.container.hasAttribute('data-background') || !!step.container.closest('check-step[data-background]')
}

// Order steps in the UI according to their step number
function moveStep(stepContainer: Element) {
  // if descendant of check-step element (new non-postback UI)
  const isCheckStepChild = stepContainer.parentNode?.nodeName === 'CHECK-STEP'

  // if the stepContainer is a child of a check-step, we want to move the check-step parent
  const elementToMove = isCheckStepChild ? stepContainer.parentElement! : stepContainer
  const stepNumber = getStepNumber(elementToMove)

  const siblingElements = [...elementToMove.parentElement!.children]
  for (const siblingElement of siblingElements) {
    if (siblingElement === elementToMove) break
    const siblingStepNumber = getStepNumber(siblingElement)
    if (+stepNumber < +siblingStepNumber) {
      elementToMove.parentElement!.insertBefore(elementToMove, siblingElement)
    }
  }
}

function showLoader(step: Step) {
  const {loader, chevron} = queryElements(step)
  loader.removeAttribute('hidden')
  chevron.setAttribute('hidden', 'hidden')
}

function hideLoader(step: Step) {
  const {loader, chevron} = queryElements(step)
  loader.setAttribute('hidden', 'hidden')
  chevron.removeAttribute('hidden')
}

function enableStep(step: Step) {
  const {chevron} = queryElements(step)
  chevron.style.removeProperty('visibility')
  step.container.style.removeProperty('pointer-events')
}

export async function loadCompletedLogs(
  step: Step,
  options?: {keepLoader?: boolean; scrollToBottom?: boolean; permaLink?: boolean},
) {
  options = options || {}

  if (step.state === StepState.COMPLETED_LOGS_LOADING) return
  if (step.state === StepState.COMPLETED_LOGS_LOADED) return

  const url = getCompletedLogURL(step)
  if (!url) return

  resetScrollToLogic()
  if (options.scrollToBottom || options.permaLink) openStep(step)

  const {downloadErrorMessage, downloadErrorMessageText} = queryElements(step)
  downloadErrorMessage.hidden = true
  showLoader(step)
  resetStep(step)
  enableStep(step)

  try {
    step.state = StepState.COMPLETED_LOGS_LOADING

    const response = await fetch(url)

    // if the SSO token expires, we will get an HTML response instead of logs (with an non-error status code)
    // so let's check that the response type is text not html
    const isHTMLContent = response?.headers?.get('Content-Type')?.includes('text/html')

    if (!response.ok || isHTMLContent) {
      let text

      if (response.status === 410) {
        text = 'data-removed-text'
      } else if (response.status === 404) {
        text = 'data-not-found-text'
      } else {
        text = 'data-error-text'
      }

      const message = downloadErrorMessageText.getAttribute(text)!

      downloadErrorMessageText.textContent = message
      throw new Error('Response was not OK')
    }

    const reader = response.body && response.body.getReader && response.body.getReader()
    if (reader) {
      await streamCompletedLog(step, reader, options)
    } else {
      const data = await response.text()
      await appendLogs(step, data, options)
    }

    step.state = StepState.COMPLETED_LOGS_LOADED
    initializeActionExpandState(step)
    applyInitialExpandState(step)
    sendStats({incrementKey: 'ACTIONS_COMPLETED_LOG_VIEWS_SUCCEED'})

    if (options.scrollToBottom) {
      scrollToLine(step, step.parser.logLines.length - 1)
    }
  } catch (err) {
    step.state = StepState.ERROR
    downloadErrorMessage.hidden = false
    sendStats({incrementKey: 'ACTIONS_COMPLETED_LOG_VIEWS_FAIL'})
    throw err
  } finally {
    hideLoader(step)
  }
}

export async function updateStreamingLogs(step: Step, newLines: LogLike[], startLine: number | null) {
  if (getCompletedLogURL(step)) return

  if (step.state !== StepState.STREAMING) {
    // change step to in progress while we wait for API update to finish
    // avoids awkward state of logs coming in before status icon has been updated
    const checkStepElement = step.container.closest<CheckStepElement>('check-step')
    if (checkStepElement) {
      checkStepElement.setAttribute('data-in-progress', '')
      // TODO: we can hack started_at here too
      checkStepElement.update()
    }
  }
  step.state = StepState.STREAMING

  if (newLines.length === 0) return

  const firstLines = step.parser.rawLogData.length === 0
  const container = fullLogsContainer()
  const lastElement = findLineElement(step, step.parser.logLines.length - 1)
  const lastElementBottom = lastElement
    ? lastElement.getBoundingClientRect().bottom
    : container.getBoundingClientRect().bottom
  const lastElementInView = isFullscreen()
    ? lastElementBottom < container.getBoundingClientRect().bottom
    : lastElementBottom < fullLogsContainerHeight()

  const shouldScroll = updateAutoScrollState(lastElementInView)

  await appendLogs(step, newLines, {startLine})

  const {loader, logsContainer, downloadErrorMessage, animation} = queryElements(step)
  enableStep(step)

  // Remove the loader and display the logs
  if (step.parser.rawLogData.length > 0 && !loader.getAttribute('hidden')) {
    loader.setAttribute('hidden', 'hidden')
    logsContainer.hidden = false
  }
  // Hide any previous error message
  downloadErrorMessage.hidden = true
  // Improves the UX when we start receiving log lines but the partial
  // for the step summary hasn't loaded yet
  if (animation) animation.removeAttribute('hidden')

  if (firstLines) {
    if (!isBackgroundStep(step)) {
      openStep(step)
      sendStats({incrementKey: 'ACTIONS_STREAMING_LOG_VIEWS_SUCCEED'})
    }
  }

  if (isOpen(step) && shouldScroll) {
    scrollToLine(step, step.parser.logLines.length - 1)
  }
}

async function streamCompletedLog(
  step: Step,
  reader: ReadableStreamDefaultReader<unknown>,
  options?: {keepLoader?: boolean; scrollToBottom?: boolean},
) {
  let bytes = 0
  let truncated = false
  const utf8Decoder = new TextDecoder('utf-8')

  // Instrumentation to quantify the progressive-ingestion win. `startTime` anchors
  // both timings; `firstContentAt` marks when the first batch has been materialized
  // into the DOM (the metric progressive ingestion is designed to move), and
  // `flushCount` distinguishes the progressive path (many bounded flushes) from the
  // buffered path (a single whole-log flush).
  const startTime = performance.now()
  let firstContentAt = 0
  let flushCount = 0
  // Progressive ingestion requires BOTH flags. `actions_incremental_log_parsing`
  // makes appendData()/updateLineMetaData() scan only the newly-appended delta
  // (O(delta)); `actions_progressive_log_ingestion` opts the streamer into flushing
  // the log to the DOM in bounded batches *as it streams in*. This paints the first
  // screenful quickly instead of blocking on a single whole-log parse + cluster build
  // over the entire response — the "blank screen while the tab pins the CPU" failure
  // mode on very large logs. Each appendLogs() awaits syncDOM(), which yields between
  // clusters, so the main thread stays responsive between batches.
  //
  // If either flag is OFF we keep the original behavior: buffer every chunk and parse
  // the whole completed log in a single pass. (With incremental parsing off,
  // updateLineMetaData() re-splits and re-parses the *entire* accumulated buffer on
  // every append — O(n) per flush -> O(n^2) overall — so batched flushing would be
  // unsafe; a single pass keeps it correct. The progressive flag lets us roll back the
  // DOM-timing change independently of the parser even when incremental parsing is on.)
  const progressive =
    isFeatureEnabled('actions_incremental_log_parsing') && isFeatureEnabled('actions_progressive_log_ingestion')

  // Progressive batches keep the loader visible: forwarding the default
  // `keepLoader: false` would let `appendLogs` hide the step's loading indicator after
  // the first batch, so a stalled or slow remainder would look complete. `loadCompletedLogs`
  // hides the loader in its `finally` once the stream finishes or fails.
  const appendOptions = progressive ? {...options, keepLoader: true} : options

  const parts: string[] = []
  let pendingChars = 0

  const appendBatch = async (batch: string) => {
    await appendLogs(step, batch, appendOptions)
    flushCount += 1
    if (firstContentAt === 0) firstContentAt = performance.now()
  }

  const flush = async () => {
    if (parts.length === 0) return
    const batch = parts.join('')
    parts.length = 0
    pendingChars = 0
    await appendBatch(batch)
  }

  for (;;) {
    const chunk = await reader.read()
    if (chunk.done) break
    // chunk.value is always an Uint8Array if done === true
    const value = chunk.value == null ? new Uint8Array(0) : (chunk.value as Uint8Array)

    bytes += value.length

    if (bytes > MAX_LOG_SIZE_IN_BYTES) {
      truncated = true
      break
    }

    // `stream: true` keeps multi-byte UTF-8 sequences intact across chunk
    // boundaries; the trailing flush below emits any buffered remainder.
    const text = utf8Decoder.decode(value, {stream: true})
    parts.push(text)
    pendingChars += text.length

    // appendData() tracks the trailing partial line itself, so batches need not
    // land on line boundaries.
    if (progressive && pendingChars >= PROGRESSIVE_FLUSH_THRESHOLD) {
      await flush()
    }
  }
  const tail = utf8Decoder.decode()
  if (tail) parts.push(tail)

  if (truncated) queryElements(step).truncatedMessage.hidden = false

  if (progressive) {
    await flush()
  } else {
    await appendBatch(parts.join(''))
  }

  reportCompletedLogMetrics({
    mode: progressive ? 'progressive' : 'buffered',
    truncated,
    bytes,
    flushCount,
    startTime,
    firstContentAt,
  })
}

// Emits the completed-log rendering metrics to Datadog so the progressive-ingestion
// rollout can be quantified.
function reportCompletedLogMetrics(sample: CompletedLogMetricSample) {
  for (const metric of buildCompletedLogMetrics(sample)) {
    sendCustomMetric(metric)
  }
}

interface CompletedLogMetricSample {
  mode: 'progressive' | 'buffered'
  truncated: boolean
  bytes: number
  flushCount: number
  startTime: number
  firstContentAt: number
}

// Buckets a byte count into a small, fixed set of labels so the timing/flush metrics can
// be sliced by log size (e.g. compare progressive vs buffered `time_to_first_content` on
// `xl` logs) without exploding tag cardinality — raw `size_bytes` stays a distribution.
export function sizeBucket(bytes: number): 'xs' | 'sm' | 'md' | 'lg' | 'xl' {
  if (bytes < 256 * 1024) return 'xs' // < 256Kb (≤ 1 flush)
  if (bytes < 1024 * 1024) return 'sm' // 256Kb–1Mb
  if (bytes < 4 * 1024 * 1024) return 'md' // 1Mb–4Mb
  if (bytes < 16 * 1024 * 1024) return 'lg' // 4Mb–16Mb
  return 'xl' // > 16Mb
}

// Pure builder for the completed-log metric payloads, kept separate from the emission
// side effect so the tagging/gating logic can be unit-tested deterministically.
//
// Segment by the `mode` tag to compare the progressive path against the buffered
// baseline: `time_to_first_content` is the headline win (buffered paints nothing until
// the single final flush, progressive paints the first screenful mid-stream), while
// `render_duration` guards against total-time regressions and `flush_count` /
// `size_bytes` give the context needed to interpret both. The `size_bucket` tag lets the
// timings be read against log volume directly (the win should scale with size).
export function buildCompletedLogMetrics(
  {mode, truncated, bytes, flushCount, startTime, firstContentAt}: CompletedLogMetricSample,
  now: number = performance.now(),
): Array<Parameters<typeof sendCustomMetric>[0]> {
  const tags = {mode, truncated: String(truncated), size_bucket: sizeBucket(bytes)}
  const metrics: Array<Parameters<typeof sendCustomMetric>[0]> = []

  // Only meaningful when a batch actually painted mid-stream (0 means the log was empty).
  if (firstContentAt > 0) {
    metrics.push({name: 'ACTIONS_COMPLETED_LOG_TIME_TO_FIRST_CONTENT', value: firstContentAt - startTime, tags})
  }

  metrics.push({name: 'ACTIONS_COMPLETED_LOG_RENDER_DURATION', value: now - startTime, tags})
  metrics.push({name: 'ACTIONS_COMPLETED_LOG_FLUSH_COUNT', value: flushCount, tags})
  metrics.push({name: 'ACTIONS_COMPLETED_LOG_SIZE_BYTES', value: bytes, tags})

  return metrics
}

function getStepNumber(stepDetails: Element): number {
  const attr = stepDetails.tagName === 'CHECK-STEP' ? 'data-number' : 'data-step-number'
  const num = stepDetails.getAttribute(attr)
  if (!num) return -1
  return +num
}

function resetStep(step: Step) {
  step.parser.setData('')
  step.clusters = []
  queryElements(step).logsContainer.textContent = ''
}

// We block virtualizing/materializing while scrolling. There are cases where we want to reset this logic.
function resetScrollToLogic() {
  setElementToScroll(null)
}

async function appendLogs(
  step: Step,
  data: string | LogLike[],
  options?: {startLine?: number | null; keepLoader?: boolean; scrollToBottom?: boolean},
) {
  options = options || {}
  const {logsContainer} = queryElements(step)
  const lastCluster = step.clusters[step.clusters.length - 1]
  let startIndex = lastCluster ? lastCluster.startIndex : 0

  const lastLine = step.parser.logLines.length - 1
  if (typeof data === 'string') {
    step.parser.appendData(data)
  } else {
    step.parser.appendLines(data, options.startLine)
  }
  renderLines(step, [lastLine]) // re-render previous last line, just in case it's changed

  // Re-render action start lines that received conclusion data from end-action markers
  // parsed in this chunk. Without this, action bars show spinners until a manual re-render.
  const newCompletedActions = step.parser.completedActionLineIndices.slice(step.lastCompletedActionCount)
  step.lastCompletedActionCount = step.parser.completedActionLineIndices.length
  if (newCompletedActions.length > 0) {
    renderLines(step, newCompletedActions)
  }

  // Divide the log lines in clusters
  while (startIndex < step.parser.logLines.length) {
    const endIndex = Math.min(startIndex + LINES_PER_CLUSTER, step.parser.logLines.length) - 1

    if (lastCluster && lastCluster.startIndex === startIndex) {
      if (lastCluster.visible) {
        // appendLines needs the previous endIndex to know where the new lines start,
        // so extend the range only after appending.
        appendLines(step, lastCluster, lastCluster.endIndex + 1, endIndex)
        lastCluster.endIndex = endIndex
      } else {
        // Extend the range BEFORE estimating, otherwise visibleLineCount/height are
        // computed over the old (shorter) range. If the old range was fully collapsed
        // (visibleLineCount 0) but the newly streamed lines are visible, a stale 0
        // would make renderCluster skip them indefinitely.
        lastCluster.endIndex = endIndex
        estimateClusterHeight(step, lastCluster)
      }
    } else {
      const wrapper = document.createElement('div')
      const cluster: Cluster = {
        startIndex,
        endIndex,
        wrapper,
        visible: false,
      }
      estimateClusterHeight(step, cluster)
      step.clusters.push(cluster)
    }

    startIndex += LINES_PER_CLUSTER
  }
  logsContainer.hidden = false
  if (!options.keepLoader) hideLoader(step)
  await syncDOM(step, true)
}

// For a very big log just the fact of adding many empty wrappers at the same time may freeze the browser.
// With this function we add wrappers one at a time.
async function syncDOM(step: Step, render: boolean) {
  const {logsContainer} = queryElements(step)
  const count = logsContainer.childElementCount
  // `count` can exceed `clusters.length` when a concurrent reset (e.g. resetStep on a step
  // transitioning to completed logs) shrinks `step.clusters` while a previously scheduled
  // syncDOM loop is still pending. Bail whenever the DOM has caught up to (or passed) the
  // cluster count to avoid indexing past the end of the array.
  if (count >= step.clusters.length) return

  const cluster = step.clusters[count]!
  logsContainer.append(cluster.wrapper)

  // We just want to render the first clusters (the ones that are visible).
  // As soon as there's some that is not visible, we stop calculating their visibility
  // to speed up the process.
  if (render) renderCluster(step, cluster)

  return new Promise<void>(resolve => {
    setTimeout(async () => {
      await syncDOM(step, render && cluster.visible)
      resolve()
    }, 0)
  })
}

/**
 * Single source of truth for whether a log line is currently renderable, i.e.
 * whether it produces a visible DOM row. Used by BOTH `estimateClusterHeight`
 * (to count reserved height) and `lineTemplate` (to decide whether to render),
 * so a cluster's reserved placeholder height always matches what actually
 * renders. Keeping these in sync avoids reserving height for lines that render
 * as zero-height hidden nodes (e.g. filtered skipped-action bars), which would
 * reintroduce the blank space / scroll jumps this virtualization prevents.
 */
function isLineRenderable(step: Step, line: LogParser['logLines'][number]): boolean {
  // End-action markers carry metadata only and never render.
  if (line.type === NodeType.EndAction) return false

  // Skipped composite steps with no content are filtered out to reduce noise.
  if (line.isActionStart && line.action?.conclusion === 'skipped') {
    const hasContent = line.actionEndLineIndex != null ? line.actionEndLineIndex - line.lineIndex > 1 : false
    if (!hasContent) return false
  }

  // Hidden inside a collapsed group. Group headers don't have .group set on them
  // (only their children do), so they stay visible.
  const groupIndex = line.group?.lineIndex
  if (groupIndex != null && !step.expandedGroups.has(groupIndex)) return false

  // Hidden inside a collapsed action, unless it's the action-start bar itself
  // (those stay visible so the user can click to expand).
  const actionIndex = line.action?.lineIndex
  if (actionIndex != null && !line.isActionStart && !step.expandedActions.has(actionIndex)) return false

  // Hidden when any ancestor action is collapsed (nested actions).
  if (line.parentActionLineIndices) {
    for (const parentIndex of line.parentActionLineIndices) {
      if (!step.expandedActions.has(parentIndex)) return false
    }
  }

  return true
}

/**
 * Estimates the pixel height of an off-screen cluster for the virtual scroll layout.
 * Counts renderable lines (see `isLineRenderable`), then sets the cluster wrapper's
 * height so the scrollbar reflects total content size.
 */
function estimateClusterHeight(step: Step, cluster: Cluster) {
  let i = cluster.startIndex
  let visibleLineCount = 0

  while (i <= cluster.endIndex) {
    const line = step.parser.logLines[i++]!
    if (isLineRenderable(step, line)) visibleLineCount++
  }

  // Cache the renderable-line count so the virtual scroller can cheaply skip
  // collapsed clusters (visibleLineCount === 0) without re-scanning lines.
  cluster.visibleLineCount = visibleLineCount

  if (!cluster.visible) {
    const estHeight = visibleLineCount * ESTIMATED_LINE_LENGTH
    cluster.height = estHeight
    cluster.wrapper.style.height = `${estHeight}px`
  }
}

/** Returns the scrollable log container, falling back to the document root. */
function fullLogsContainer() {
  return scrollableContainer() || document.documentElement
}

/** Returns the visible height of the log viewport, accounting for fullscreen and UX refresh modes. */
function fullLogsContainerHeight() {
  // For UX refresh, use the entire window, unless in full screen mode
  return scrollableContainer() && (!uxRefresh() || isFullscreen())
    ? scrollableContainer().offsetHeight
    : window.innerHeight
}

/**
 * Virtual scroll driver for a single cluster. Checks whether the cluster's
 * wrapper is within the visible viewport and materializes or tears down its
 * DOM content accordingly.
 */
function renderCluster(step: Step, cluster: Cluster) {
  // Collapsed regions: a cluster whose lines are all hidden (inside a collapsed
  // group/action) has nothing to show. Never materialize it. Without this, a
  // large collapsed group (e.g. 20k lines) crams hundreds of zero-height clusters
  // into the viewport, materializes them all, and expanding then re-renders every
  // line synchronously — freezing the tab. Skipping also avoids the layout reads
  // below for the (very common) collapsed case.
  if (cluster.visibleLineCount === 0) {
    if (cluster.visible) setClusterVisibility(step, cluster, false)
    return
  }

  const container = fullLogsContainer()
  const {wrapper, visible} = cluster

  const scrollTop = uxRefresh() && !isFullscreen() ? -container.getBoundingClientRect().top : container.scrollTop
  const wrapperTop = wrapper.offsetTop
  const heightBefore = wrapper.offsetHeight
  const above = scrollTop > wrapperTop + heightBefore
  const below = scrollTop + fullLogsContainerHeight() < wrapperTop

  // A cluster located entirely above the viewport anchor: if materializing or
  // virtualizing it changes its height (the estimated placeholder height rarely
  // matches the real rendered height), the visible content below it shifts and
  // the scroll position "jumps". We compensate by the height delta so the user's
  // view stays anchored while scrolling through large composite logs.
  const compensate = wrapperTop < scrollTop

  if (above || below) {
    setClusterVisibility(step, cluster, false)
  } else if (!visible) {
    setClusterVisibility(step, cluster, true)
  }

  if (compensate) {
    const delta = wrapper.offsetHeight - heightBefore
    if (delta !== 0) adjustScrollBy(delta)
  }
}

/**
 * Shifts the active scroll position by `delta` pixels using the same scroll
 * source `renderCluster` reads from, so height compensation anchors the correct
 * element: the window only in UX-refresh inline mode, otherwise the log
 * container (fullscreen and the legacy inline viewer both scroll the container).
 * Flags the adjustment so the scroll listener treats it as programmatic rather
 * than a user gesture.
 */
function adjustScrollBy(delta: number) {
  if (delta === 0) return
  suppressScrollDirection = true
  if (uxRefresh() && !isFullscreen()) {
    window.scrollBy(0, delta)
  } else {
    fullLogsContainer().scrollTop += delta
  }
}

/**
 * Materializes or tears down a cluster's DOM content. When hiding, records the
 * current height and replaces content with an empty placeholder of the same
 * size to preserve scroll position. Preserves visibility if the user has an
 * active text selection within the cluster.
 */
function setClusterVisibility(step: Step, cluster: Cluster, visible: boolean) {
  const {wrapper} = cluster
  const selection = window.getSelection()

  if (selection && !selection.isCollapsed && selection.containsNode(wrapper, true)) visible = true
  if (visible === cluster.visible) return

  if (visible) {
    appendLines(step, cluster, cluster.startIndex, cluster.endIndex)
    wrapper.style.height = 'auto'
  } else {
    // Reserve an accurate placeholder height so tearing the cluster down doesn't
    // shift the content around it (which makes the scroll position jump/cascade).
    //  - Collapsed clusters (all lines hidden) MUST reserve 0, otherwise a
    //    just-collapsed region leaves blank space below it.
    //  - Content clusters reserve their real rendered height, measured while still
    //    on screen. Log lines wrap (white-space: pre-wrap), so the line-count
    //    estimate is not reliable enough to avoid scroll jumps here.
    if (cluster.visibleLineCount === 0) {
      cluster.height = 0
    } else if (cluster.visible) {
      cluster.height = wrapper.offsetHeight || cluster.height
    }
    wrapper.style.height = `${cluster.height ?? 0}px`
    wrapper.textContent = ''
  }
  cluster.visible = visible
}

/** Renders a range of log lines into a cluster's DOM using lit-html templates. */
function appendLines(step: Step, cluster: Cluster, startIndex: number, endIndex: number) {
  const lineIndexes = []
  for (let index = startIndex; index <= endIndex; index++) {
    lineIndexes.push(index)
  }
  const template = html`
    ${repeat(lineIndexes, lineIndex => {
      const tmpl = lineTemplate(step, lineIndex)
      if (!tmpl) return html`<div data-index="${lineIndex}" hidden></div>`
      return html`<div data-index="${lineIndex}">${tmpl}</div>`
    })}
  `
  const element = document.createElement('div')
  renderHTML(template, element)
  cluster.wrapper.appendChild(element)
}

/** Runs the virtual scroll pass for a single step, updating all its clusters. */
function renderStep(step: Step) {
  if (step.state >= StepState.COMPLETED_LOGS_LOADED) {
    for (const cluster of step.clusters) {
      renderCluster(step, cluster)
    }
  }
}

/** Top-level scroll handler — renders clusters for all open steps. Pauses while actively scrolling to a target element. */
function renderSteps() {
  // Don't attempt to re-render steps while we are actively scrolling towards an element. Once the element is within the view
  // we can start processing renders again
  const scrollTarget = getElementToScroll()
  if (scrollTarget) {
    const container = fullLogsContainer()
    const scrollTop = uxRefresh() ? -container.getBoundingClientRect().top : container.scrollTop
    const elemAbove = scrollTop > scrollTarget.offsetTop + scrollTarget.offsetHeight
    const elemBelow = scrollTop + fullLogsContainerHeight() < scrollTarget.offsetTop
    if (!elemAbove && !elemBelow) {
      setElementToScroll(null)
    }
  } else {
    for (const step of getUnorderedSteps()) {
      if (isOpen(step)) renderStep(step)
    }
  }
}

/** Queries the step container for key DOM elements (log display, loader, error messages, etc.). */
function queryElements(step: Step) {
  const logDisplayElement = step.container.querySelector<HTMLElement>('.js-checks-log-display')!
  return {
    logsContainer: logDisplayElement.querySelector<HTMLElement>('.js-checks-log-display-container')!,
    loader: step.container.querySelector<SVGElement>('.js-check-step-loader')!,
    downloadErrorMessage: logDisplayElement.querySelector<HTMLElement>('.js-checks-log-display-error')!,
    downloadErrorMessageText: logDisplayElement.querySelector<HTMLElement>('.js-checks-log-display-error-text')!,
    animation: step.container.querySelector('.js-check-step-icon-animation'),
    truncatedMessage: logDisplayElement.querySelector<HTMLElement>('.js-checks-log-display-truncated')!,
    chevron: step.container.querySelector<SVGElement>('.js-check-step-chevron')!,
  }
}

/** Returns true when the log panel is in fullscreen mode (detected by inline position style). */
export function isFullscreen() {
  const checkSteps = document.querySelector<HTMLElement>('.js-check-steps')
  return !!checkSteps?.style?.position
}

/** Highlights matching lines in a step and optionally scrolls to the first match. */
export function searchStep(step: Step, text: string, selectFirstLine: boolean) {
  cancelSearchStep(step)

  const searchLineIndexes = step.parser.search(text, selectFirstLine)
  step.searchLineIndexes = searchLineIndexes
  if (selectFirstLine && searchLineIndexes.length > 0) {
    renderLines(step, step.searchLineIndexes)
    scrollToLine(step, searchLineIndexes[0]!, {select: true})
  }
  return searchLineIndexes
}

/** Clears search highlights and re-renders previously highlighted lines. */
export function cancelSearchStep(step: Step) {
  step.parser.cancelSearch()
  renderLines(step, step.searchLineIndexes)
  step.searchLineIndexes = []
}

/** Re-renders a single line in-place, or hides it if the template returns null. */
function renderLine(step: Step, index: number, element: HTMLElement) {
  const template = lineTemplate(step, index)
  if (template === null) {
    element.hidden = true
    return
  }
  element.hidden = false
  renderHTML(template, element)
}

/**
 * Returns the rendered HTML for a single log line, or null if the line should
 * be hidden (e.g. inside a collapsed group/action, an end-action marker, or a
 * filtered skipped-action bar). Uses the same `isLineRenderable` predicate as
 * `estimateClusterHeight` so reserved height matches what actually renders.
 */
function lineTemplate(step: Step, index: number) {
  const line = step.parser.logLines[index]!

  if (!isLineRenderable(step, line)) return null

  const groupExpanded = line.isGroup && step.expandedGroups.has(index)
  const actionExpanded = line.isActionStart && step.expandedActions.has(index)
  return step.parser.renderTemplate(line, !!groupExpanded, false, !!actionExpanded)
}

/** Re-renders a batch of lines by their indexes, looking up each element in the DOM. */
function renderLines(step: Step, lineIndexes: number[]) {
  for (const index of lineIndexes) {
    const element = findLineElement(step, index)
    if (element) renderLine(step, index, element)
  }
}

/** Finds a line's DOM element by its data-index attribute. */
function findLineElement(step: Step, lineIndex: number) {
  return step.container.querySelector(`[data-index='${lineIndex}']`) as HTMLElement
}

/**
 * Scrolls a specific log line into view. Materializes surrounding clusters first
 * to prevent layout shifts, expands any collapsed group/action containing the line,
 * and optionally highlights it as the selected permalink line.
 */
export function scrollToLine(step: Step, lineIndex: number, options?: {select?: boolean}) {
  const {select} = options || {}
  if (lineIndex < 0 || step.clusters.length === 0) return

  openStep(step)

  // If the target sits inside a collapsed group/action, expand it up front (and
  // refresh the cached visibleLineCount/heights) so its cluster is no longer
  // treated as collapsed when we materialize below. Doing this first lets the
  // materialization pass skip genuinely-collapsed clusters without dropping the
  // target.
  const targetLine = step.parser.logLines[lineIndex]
  const targetGroupIndex = targetLine?.group?.lineIndex
  const targetActionIndex = targetLine?.action?.lineIndex
  const needsExpand =
    (targetGroupIndex != null && !step.expandedGroups.has(targetGroupIndex)) ||
    (targetActionIndex != null && !targetLine?.isActionStart && !step.expandedActions.has(targetActionIndex)) ||
    !!targetLine?.parentActionLineIndices?.some(parentIndex => !step.expandedActions.has(parentIndex))
  if (needsExpand) {
    expandGroup(step, lineIndex)
    expandAction(step, lineIndex)
    for (const cluster of step.clusters) estimateClusterHeight(step, cluster)
  }

  // Before scrolling ensure that we have materialized at least +/- 50 lines on either side
  // This ensures we do not materialize another cluster once we scroll to the index
  // which would change the visible elements. Collapsed clusters (visibleLineCount 0)
  // are skipped — they have nothing to show and would only recreate hidden DOM nodes.
  const skipVirtualization = step.clusters.length < 100
  for (const cluster of step.clusters) {
    if (cluster.visibleLineCount === 0) continue
    if (
      skipVirtualization ||
      (lineIndex - LINES_PER_CLUSTER <= cluster.endIndex && lineIndex + LINES_PER_CLUSTER >= cluster.startIndex)
    ) {
      setClusterVisibility(step, cluster, true)
    }
  }

  // Additionally render neighboring clusters in case we step into a small cluster in betwen two other open clusters.
  const orderedSteps = getUnorderedSteps().sort((a, b) => a.number - b.number)
  const stepIndex = orderedSteps.findIndex(x => x.number === step.number)
  ensureEnoughMaterializedLines(orderedSteps, stepIndex, LINES_PER_CLUSTER - (lineIndex + 1), true)
  const linesAwayFromEnd = LINES_PER_CLUSTER - (step.clusters[step.clusters.length - 1]!.endIndex - lineIndex)
  ensureEnoughMaterializedLines(orderedSteps, stepIndex, linesAwayFromEnd, false)

  const element = findLineElement(step, lineIndex)
  if (!element) return

  setElementToScroll(element)
  // If we expanded a collapsed region above, re-render the (now visible) target line.
  if (needsExpand && select) renderLine(step, lineIndex, element)
  if (select) selectLine(element)

  // Ideally we don't do this but we only add expanded groups after the grouptoggle handler is triggered.
  setTimeout(async () => {
    // Re-check: if auto-scroll was disabled while this was queued (e.g. CI
    // Analysis partial mounted between scheduling and execution), abort unless
    // this is a user-initiated scroll (select = permalink navigation).
    if (!select && !isAutoScrollEnabled()) {
      setElementToScroll(null)
      return
    }
    getElementToScroll()?.scrollIntoView({behavior: skipVirtualization ? 'smooth' : 'auto', block: 'center'})
  }, 0)
}

/**
 * Materializes clusters in neighboring steps above or below the current step,
 * so the scroll target has enough rendered content around it to avoid jumps.
 */
function ensureEnoughMaterializedLines(
  orderedSteps: Step[],
  currentStep: number,
  linesToMaterialize: number,
  above: boolean,
) {
  let linesLeft = linesToMaterialize

  const increment = above ? -1 : 1
  let i = currentStep + increment
  while (linesLeft > 0 && ((above && i >= 0) || (!above && i < orderedSteps.length))) {
    const neighborStep = orderedSteps[i]!
    if (neighborStep.clusters?.length > 0 && isOpen(neighborStep)) {
      // Scan inward from the edge nearest the target through this step's clusters,
      // materializing visible ones until the buffer is filled. If we only checked
      // the edge cluster and it were collapsed, we'd skip the whole step's buffer
      // even when inner clusters have visible rows — leaving a gap that causes a
      // late materialization and scroll jump. Decrement by visibleLineCount (the
      // rows actually rendered) and skip collapsed clusters.
      const clusters = neighborStep.clusters
      const dir = above ? -1 : 1
      for (let c = above ? clusters.length - 1 : 0; linesLeft > 0 && c >= 0 && c < clusters.length; c += dir) {
        const cluster = clusters[c]!
        const visibleLines = cluster.visibleLineCount ?? cluster.endIndex - cluster.startIndex + 1
        if (visibleLines === 0) continue
        setClusterVisibility(neighborStep, cluster, true)
        linesLeft -= visibleLines
      }
    }
    i += increment
  }
}

/** Expands the group containing the given line so it becomes visible. */
function expandGroup(step: Step, lineIndex: number) {
  const line = step.parser.logLines[lineIndex]!
  const groupIndex = line.group?.lineIndex
  if (groupIndex == null) return
  step.expandedGroups.add(groupIndex)
  const details = step.container.querySelector(`[data-index='${groupIndex}'] details`)
  if (details) details.setAttribute('open', 'open')
}

/** Marks a line element as the selected permalink target, clearing any previous selection. */
function selectLine(element: HTMLElement) {
  for (const prev of document.querySelectorAll('.js-check-step-line.selected')) {
    prev.classList.remove('selected')
  }
  // Intentionally hidden lines (e.g. filtered skipped-action markers) have no
  // .js-check-step-line child; tolerate that instead of dereferencing null.
  element.querySelector<HTMLElement>('.js-check-step-line')?.classList.add('selected')
}

/** Finds the Step object that owns the given child DOM element. */
export function findStep(childElement: Element) {
  const container = childElement.closest<HTMLElement>('.js-checks-log-details')!
  return getUnorderedSteps().find(step => step.container === container)
}

/** Toggles a group's expand/collapse state and re-renders affected clusters. */
export function toggleGroup(step: Step, index: number, open: boolean) {
  if (open) {
    step.expandedGroups.add(index)
  } else {
    step.expandedGroups.delete(index)
  }

  const lines = step.parser.logLines.filter(line => line.group?.lineIndex === index)
  if (lines.length === 0) return

  const lineIndexes = lines.map(line => line.lineIndex)
  const minLine = lineIndexes[0]!
  const maxLine = lineIndexes[lineIndexes.length - 1]!

  // Write-only pass: update off-screen cluster height estimates and re-render
  // the lines of clusters that are already on screen. We deliberately do NOT
  // call renderCluster() here — reading layout (offsetTop/offsetHeight) right
  // after writing style.height on each cluster forces a synchronous reflow per
  // iteration, which is O(n) reflows for large actions. The single renderSteps()
  // below handles all virtualization in one batched read pass instead.
  for (const cluster of step.clusters) {
    if (cluster.startIndex <= maxLine && cluster.endIndex >= minLine) {
      estimateClusterHeight(step, cluster)
      if (cluster.visible) {
        const clusterLineIndexes = lineIndexes.filter(idx => idx >= cluster.startIndex && idx <= cluster.endIndex)
        renderLines(step, clusterLineIndexes)
      }
    }
  }

  // clusters after the group may have change visibility
  renderSteps()
}

/** Toggles an action bar's expand/collapse state and re-renders affected clusters. */
export function toggleAction(step: Step, index: number, open: boolean) {
  if (open) {
    step.expandedActions.add(index)
  } else {
    step.expandedActions.delete(index)
  }

  const startLine = step.parser.logLines[index]
  if (!startLine) return

  const endLineIndex = startLine.actionEndLineIndex ?? step.parser.logLines.length - 1

  // Write-only pass: update off-screen cluster height estimates and re-render
  // the lines of clusters that are already on screen. We deliberately do NOT
  // call renderCluster() here — reading layout (offsetTop/offsetHeight) right
  // after writing style.height on each cluster forces a synchronous reflow per
  // iteration, which is O(n) reflows for large actions. The single renderSteps()
  // below handles all virtualization in one batched read pass instead.
  for (const cluster of step.clusters) {
    if (cluster.startIndex <= endLineIndex && cluster.endIndex >= index) {
      estimateClusterHeight(step, cluster)
      if (cluster.visible) {
        const from = Math.max(index, cluster.startIndex)
        const to = Math.min(endLineIndex, cluster.endIndex)
        const clusterLineIndexes: number[] = []
        for (let i = from; i <= to; i++) clusterLineIndexes.push(i)
        renderLines(step, clusterLineIndexes)
      }
    }
  }

  renderSteps()
}

/**
 * Sets the initial expand/collapse state for all action bars in a step.
 * Failed and in-progress (no end marker yet) actions are auto-expanded;
 * everything else starts collapsed.
 */
export function initializeActionExpandState(step: Step) {
  step.expandedActions.clear()
  for (const line of step.parser.logLines) {
    if (!line.isActionStart || !line.action) continue
    const action = line.action
    // Auto-expand failed or in-progress actions
    if (action.conclusion === 'failure' || action.endLineIndex == null) {
      step.expandedActions.add(line.lineIndex)
    }
  }
}

/**
 * Applies the initial `expandedActions` set to the DOM in a single batched pass.
 *
 * This replaces looping `toggleAction(step, index, true)` over every auto-expanded
 * action on load, which was O(expandedActions x clusters) AND ran a full
 * `renderSteps()` per action. The cluster heights produced during `appendLogs`
 * assumed the collapsed state, so here we re-estimate every cluster once, re-render
 * the lines of clusters that are already on screen (their DOM still reflects the
 * collapsed state), and run a single virtualization pass.
 */
function applyInitialExpandState(step: Step) {
  if (step.expandedActions.size === 0) return

  for (const cluster of step.clusters) {
    estimateClusterHeight(step, cluster)
    if (cluster.visible) {
      const clusterLineIndexes: number[] = []
      for (let i = cluster.startIndex; i <= cluster.endIndex; i++) clusterLineIndexes.push(i)
      renderLines(step, clusterLineIndexes)
    }
  }

  renderSteps()
}

/** Expands the action (and any parent actions) containing the given line so it becomes visible. */
function expandAction(step: Step, lineIndex: number) {
  const line = step.parser.logLines[lineIndex]!
  const actionIndex = line.action?.lineIndex
  if (actionIndex != null) {
    step.expandedActions.add(actionIndex)
    const details = step.container.querySelector(`[data-index='${actionIndex}'] .js-checks-action-region`)
    if (details) details.setAttribute('open', 'open')
  }
  // Also expand parent actions
  if (line.parentActionLineIndices) {
    for (const parentIndex of line.parentActionLineIndices) {
      step.expandedActions.add(parentIndex)
      const details = step.container.querySelector(`[data-index='${parentIndex}'] .js-checks-action-region`)
      if (details) details.setAttribute('open', 'open')
    }
  }
}

/** Scrolls to and optionally selects a line by its 1-based permalink number. */
export function selectPermalinkLine(step: Step, permalinkLine: number, scroll: boolean) {
  const lineIndex = step.parser.indexFromNumber(permalinkLine)
  if (lineIndex < 0) return

  if (scroll) {
    scrollToLine(step, lineIndex, {select: true})
  } else {
    const element = findLineElement(step, lineIndex)
    if (element) selectLine(element)
  }
}

/** Toggles the log panel between fullscreen and inline mode, adjusting scroll listeners and sticky headers. */
function toggleFullScreen() {
  const checkSteps = document.querySelector<HTMLElement>('.js-check-steps')!

  setFullScreenKeyboardShortcuts()
  resetScrollState()

  if (isFullscreen()) {
    // Turn the logs container back into what it was before becoming full screen
    if (uxRefresh()) {
      const container = fullLogsContainer()
      const stepHeaders = container.querySelectorAll<HTMLElement>('.js-check-step-summary')
      const logToolbar = document.querySelector<HTMLElement>('.js-checks-log-toolbar')!

      scrollableContainer()?.removeEventListener('scroll', scrollListener)
      /* eslint-disable-next-line github/prefer-observers */
      document.addEventListener('scroll', scrollListener)

      container.classList.remove('overflow-y-scroll')
      logToolbar.classList.add('uxr_CheckRun-header')

      for (const stepHeader of stepHeaders) {
        stepHeader.classList.add('uxr_CheckStep-header')
        stepHeader.classList.remove('top-0')
        stepHeader.style.removeProperty('sticky')
      }
    }

    for (const prop of ['position', 'top', 'left', 'right', 'bottom', 'zIndex']) {
      checkSteps.style.removeProperty(prop)
    }
    checkSteps.classList.add('rounded-md-1')
  } else {
    // Turn the logs container into a full screen scrollable element
    if (uxRefresh()) {
      const container = fullLogsContainer()
      const stepHeaders = container.querySelectorAll<HTMLElement>('.js-check-step-summary')
      const logToolbar = document.querySelector<HTMLElement>('.js-checks-log-toolbar')!

      /* eslint-disable-next-line github/prefer-observers */
      scrollableContainer()?.addEventListener('scroll', scrollListener)
      document.removeEventListener('scroll', scrollListener)

      container.classList.add('overflow-y-scroll')
      logToolbar.classList.remove('uxr_CheckRun-header')

      for (const stepHeader of stepHeaders) {
        stepHeader.classList.remove('uxr_CheckStep-header')
        stepHeader.classList.add('top-0')
        stepHeader.style.position = 'sticky'
      }
    }

    checkSteps.style.position = 'fixed'
    checkSteps.style.left = '0'
    checkSteps.style.right = '0'
    checkSteps.style.top = '0'
    checkSteps.style.bottom = '0'
    checkSteps.style.zIndex = '1000'
    checkSteps.classList.remove('rounded-md-1')
  }
  calculateContainerHeights()
  renderSteps()
}

/** Updates hotkey bindings for fullscreen toggle — Escape only available when already fullscreen. */
function setFullScreenKeyboardShortcuts() {
  const fullScreenToggle = document.querySelector<HTMLElement>('.js-checks-log-fullscreen')!
  uninstall(fullScreenToggle)

  if (areCharacterKeyShortcutsEnabled()) {
    fullScreenToggle.setAttribute('data-hotkey', isFullscreen() ? 'Shift+F' : 'Shift+F,Escape')
  } else {
    fullScreenToggle.setAttribute('data-hotkey', isFullscreen() ? '' : 'Escape')
  }
  /* eslint-disable-next-line github/no-dataset */
  install(fullScreenToggle, fullScreenToggle.dataset.shortcut)
}

on('change', '.js-checks-log-fullscreen', toggleFullScreen)
